import { Grid, Paper, Typography } from "@mui/material";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { TemperatureField } from "components/LLMs/TemperatureField";
import type {
    KboSettings,
    KboPromptsSettings,
    KboSystemPromptSettings,
} from "components/KBo/Models/KboSettings";
import React, { useCallback } from "react";
import { SettingsFieldLabel } from "./SettingsFieldLabel";
import { AvailableTags } from "./AvailableTags";

interface Props {
    systemPrompt?: KboSystemPromptSettings;
    prompts?: KboPromptsSettings;
    onChange: (patch: {
        systemPrompt?: KboSystemPromptSettings;
        prompts?: KboPromptsSettings;
    }) => void;
    defaults?: KboSettings | null;
    templateTags?: Record<string, string[]>;
}

export function KBoCoreConfiguration({
    systemPrompt,
    prompts,
    onChange,
    defaults,
    templateTags,
}: Props) {
    const handleSystemPromptChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            onChange({
                systemPrompt: {
                    ...systemPrompt,
                    organizationContext: e.target.value || null,
                },
            });
        },
        [systemPrompt, onChange],
    );

    const handlePromptsChange = useCallback(
        (field: keyof KboPromptsSettings, value: string | number | null) => {
            onChange({
                prompts: { ...prompts, [field]: value },
            });
        },
        [prompts, onChange],
    );

    return (
        <Grid container direction="column" rowGap={3}>
            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">
                                Organization Context
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Additional context appended to the KBo system
                                prompt for this organization.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <SettingsFieldLabel
                                label="Organization Context"
                                isDefault={
                                    systemPrompt?.organizationContext == null
                                }
                                onReset={() =>
                                    onChange({
                                        systemPrompt: {
                                            ...systemPrompt,
                                            organizationContext: null,
                                        },
                                    })
                                }
                            />
                            <AcxMainTextField
                                id="kbo-organization-context"
                                placeholderText="e.g., This organization focuses on Medicare Part D calls..."
                                fullWidth
                                multiline
                                rowsMin={3}
                                rowsMax={8}
                                value={
                                    systemPrompt?.organizationContext ??
                                    defaults?.systemPrompt
                                        ?.organizationContext ??
                                    ""
                                }
                                onChange={handleSystemPromptChange}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">
                                System Prompt
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                The main system prompt template that defines
                                KBo's behavior and personality.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <SettingsFieldLabel
                                label="System Prompt Template"
                                isDefault={
                                    prompts?.systemPromptTemplate == null
                                }
                                onReset={() =>
                                    handlePromptsChange(
                                        "systemPromptTemplate",
                                        null,
                                    )
                                }
                            />
                            <AcxMainTextField
                                id="kbo-system-prompt-template"
                                fullWidth
                                multiline
                                rowsMin={6}
                                rowsMax={20}
                                value={
                                    prompts?.systemPromptTemplate ??
                                    defaults?.prompts
                                        ?.systemPromptTemplate ??
                                    ""
                                }
                                onChange={(e) =>
                                    handlePromptsChange(
                                        "systemPromptTemplate",
                                        e.target.value || null,
                                    )
                                }
                            />
                            <AvailableTags
                                tags={templateTags?.systemPromptTemplate}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">
                                Authenticx Background
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Background information about Authenticx
                                injected into the system prompt.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <SettingsFieldLabel
                                label="Authenticx Background"
                                isDefault={
                                    prompts?.authenticxBackground == null
                                }
                                onReset={() =>
                                    handlePromptsChange(
                                        "authenticxBackground",
                                        null,
                                    )
                                }
                            />
                            <AcxMainTextField
                                id="kbo-authenticx-background"
                                fullWidth
                                multiline
                                rowsMin={4}
                                rowsMax={12}
                                value={
                                    prompts?.authenticxBackground ??
                                    defaults?.prompts
                                        ?.authenticxBackground ??
                                    ""
                                }
                                onChange={(e) =>
                                    handlePromptsChange(
                                        "authenticxBackground",
                                        e.target.value || null,
                                    )
                                }
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">
                                Chat Name Generation
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Prompt used to generate conversation names from
                                the user's first message.
                            </Typography>
                        </Grid>
                        <Grid
                            container
                            item
                            columnGap={2}
                            flexWrap="nowrap"
                            alignItems="flex-start"
                        >
                            <Grid item xs>
                                <SettingsFieldLabel
                                    label="Prompt"
                                    isDefault={
                                        prompts?.chatNameGenerationPrompt ==
                                        null
                                    }
                                    onReset={() =>
                                        handlePromptsChange(
                                            "chatNameGenerationPrompt",
                                            null,
                                        )
                                    }
                                />
                                <AcxMainTextField
                                    id="kbo-chat-name-prompt"
                                    fullWidth
                                    multiline
                                    rowsMin={3}
                                    rowsMax={10}
                                    value={
                                        prompts?.chatNameGenerationPrompt ??
                                        defaults?.prompts
                                            ?.chatNameGenerationPrompt ??
                                        ""
                                    }
                                    onChange={(e) =>
                                        handlePromptsChange(
                                            "chatNameGenerationPrompt",
                                            e.target.value || null,
                                        )
                                    }
                                />
                                <AvailableTags
                                    tags={
                                        templateTags?.chatNameGenerationPrompt
                                    }
                                />
                            </Grid>
                            <Grid item xs={2}>
                                <SettingsFieldLabel
                                    label="Temperature"
                                    isDefault={
                                        prompts?.chatNameGenerationTemperature ==
                                        null
                                    }
                                    onReset={() =>
                                        handlePromptsChange(
                                            "chatNameGenerationTemperature",
                                            null,
                                        )
                                    }
                                />
                                <TemperatureField
                                    id="kbo-chat-name-temp"
                                    value={
                                        prompts?.chatNameGenerationTemperature ??
                                        null
                                    }
                                    placeholder={
                                        defaults?.prompts
                                            ?.chatNameGenerationTemperature ??
                                        null
                                    }
                                    onChange={(temperature) =>
                                        handlePromptsChange(
                                            "chatNameGenerationTemperature",
                                            temperature,
                                        )
                                    }
                                />
                            </Grid>
                        </Grid>

                    </Grid>
                </Paper>
            </Grid>

            <Grid item>
                <Paper>
                    <Grid
                        container
                        direction="column"
                        rowGap={2}
                        padding={3}
                    >
                        <Grid item>
                            <Typography variant="h6">
                                Skill Testing Prompt
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                Prompt used to evaluate a KBo skill against an
                                imported test dataset.
                            </Typography>
                        </Grid>
                        <Grid item>
                            <SettingsFieldLabel
                                label="Test Prompt"
                                isDefault={prompts?.skillTestPrompt == null}
                                onReset={() =>
                                    handlePromptsChange(
                                        "skillTestPrompt",
                                        null,
                                    )
                                }
                            />
                            <AcxMainTextField
                                id="kbo-skill-test-prompt"
                                fullWidth
                                multiline
                                rowsMin={6}
                                rowsMax={18}
                                value={
                                    prompts?.skillTestPrompt ??
                                    defaults?.prompts?.skillTestPrompt ??
                                    ""
                                }
                                onChange={(e) =>
                                    handlePromptsChange(
                                        "skillTestPrompt",
                                        e.target.value || null,
                                    )
                                }
                            />
                            <AvailableTags
                                tags={templateTags?.skillTestPrompt}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
}
